import React from "react";
import {
  View,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Text,
  ActivityIndicator,
  Dimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";

interface ThemeColors {
  brand?: { primary?: string };
  border?: { default?: string };
  background?: { surface?: string };
  text?: { primary?: string; secondary?: string };
}

interface SiteEvalRedirectActionProps {
  isSubmitting: boolean;
  isPdfExporting: boolean;
  status: string;
  selectedRecordId: number | string | undefined;
  userEmail: string;
  onSave: () => void;
  onComplete: () => void;
  onExportPdf: () => void;
  onBack: () => void;
  isDark?: boolean;
  colors?: ThemeColors;
}

const isMobileView = () => {
  const windowWidth = Platform.OS === 'web' ? (typeof window !== 'undefined' ? window.innerWidth : 800) : Dimensions.get('window').width;
  // Consider screens narrower than 768px as mobile; treat iPad (iOS + width >= 768) separately
  return windowWidth < 768 || Platform.OS === 'android' || (Platform.OS === 'ios' && windowWidth < 768);
};

// Detect iPad specifically (iOS + width >= 768)
const isIPadView = () => {
  const windowWidth = Platform.OS === 'web' ? (typeof window !== 'undefined' ? window.innerWidth : 800) : Dimensions.get('window').width;
  return Platform.OS === 'ios' && windowWidth >= 768;
};

const SiteEvalRedirectAction: React.FC<SiteEvalRedirectActionProps> = ({
  isSubmitting,
  isPdfExporting,
  status,
  selectedRecordId,
  userEmail,
  onSave,
  onComplete,
  onExportPdf,
  onBack,
  isDark = false,
  colors = {},
}) => {
  const isCompleted = status === "Completed";
  const isPdfExportEnabled = isCompleted && selectedRecordId && userEmail;
  const isIpad = isIPadView();
  // Treat iPad as non-mobile so we render horizontal layout on iPad
  const isMobile = isMobileView() && !isIpad;

  const styles = StyleSheet.create({
    actionRow: {
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "stretch" : "center",
      // borderTopWidth: 1,
      borderTopColor: isDark ? "rgba(255,255,255,0.1)" : "#dee2e6",
      paddingTop: 24,
      width: "100%",
      gap: isMobile ? 16 : 0,
    },
    leftButtonSection: {
      flex: isMobile ? 0 : 1,
      alignItems: isMobile ? "center" : "flex-start",
      width: isMobile ? "100%" : "auto",
    },
    rightButtonSection: {
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 10 : 10,
      // On tablet / web row layouts, align buttons vertically centered and push them to the right
      alignItems: isMobile ? "stretch" : "center",
      justifyContent: isMobile ? "flex-start" : "flex-end",
      width: isMobile ? "100%" : "auto",
      // Add extra right padding on iPad so buttons don't hug the screen edge
      // paddingRight: isIpad ? 50 : 0,

    },
    customButton: {
      paddingVertical: 8,
      paddingHorizontal: 24,
      borderRadius: 24,
      backgroundColor: "#ccc",
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
      justifyContent: 'center',
      alignItems: 'center',
      height: 40,
      minWidth: Platform.OS === 'web' && !isMobile ? 140 : undefined,
      width: isMobile ? "100%" : "auto",
    },
    backButton: isDark ? {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: '#005CBC',
      marginRight: isMobile ? 0 : 10,
      // Give Back button extra minimum width on iPad so it doesn't appear too small
      minWidth: isIpad ? 160 : undefined,
    } : {
      backgroundColor: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#034895',
      marginRight: isMobile ? 0 : 10,
      // Give Back button extra minimum width on iPad so it doesn't appear too small
      minWidth: isIpad ? 160 : undefined,
    },
    saveButton: isDark ? {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: '#005CBC',
      marginRight: isMobile ? 0 : 10,
    } : {
      backgroundColor: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#034895',
      marginRight: isMobile ? 0 : 10,
    },
    completeButton: {
      backgroundColor: '#034895',
      borderColor: '#034895',
    },
    pdfExportButton: {
      backgroundColor: "#034895",
    },
    disabledButton: {
      opacity: 1,
      backgroundColor: isDark ? "#333333" : "#F5F5F5",
      borderWidth: 1,
      borderColor: isDark ? "#555555" : "#DADADA",
      borderRadius: 24,
      paddingVertical: 8,
      paddingHorizontal: 24,
      height: 40,
      shadowOpacity: 0.1,
      alignItems: 'center',
      justifyContent: 'center',
      ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
    },
    buttonContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      flexWrap: "nowrap",
    },
    icon: {
      marginRight: 8,
    } as any,
    buttonText: {
      color: "white",
      fontSize: 16,
      fontWeight: "700",
      fontFamily: Platform.select({ 
        web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
        ios: 'Inter', 
        android: 'Inter' 
      }),
      lineHeight: 22,
      textAlign: 'center',
      flexShrink: 1,
      numberOfLines: 1,
    } as any,
    backButtonText: {
      color: isDark ? '#005CBC' : '#034895',
      fontSize: 16,
      fontWeight: "700",
      fontFamily: Platform.select({ 
        web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
        ios: 'Inter', 
        android: 'Inter' 
      }),
      lineHeight: 22,
      textAlign: 'center',
      flexShrink: 1,
      numberOfLines: 1,
    } as any,
    buttonTextDisabled: {
      color: isDark ? "#888888" : "#B1B1B1",
      fontSize: 16,
      fontWeight: "700",
      fontFamily: Platform.select({ 
        web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
        ios: 'Inter', 
        android: 'Inter' 
      }),
      lineHeight: 22,
      textAlign: 'center',
      flexShrink: 1,
      numberOfLines: 1,
    } as any,
    saveButtonText: {
      color: isDark ? '#005CBC' : '#034895',
      fontSize: 16,
      fontWeight: "700",
      fontFamily: Platform.select({ 
        web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif', 
        ios: 'Inter', 
        android: 'Inter' 
      }),
      lineHeight: 22,
      textAlign: 'center',
      flexShrink: 1,
      numberOfLines: 1,
    } as any,
  });

  return (
    <View style={styles.actionRow}>
      {/* Left side - Back button */}
      <View style={styles.leftButtonSection}>
        <TouchableOpacity
          style={[styles.customButton, styles.backButton]}
          onPress={onBack}
        >
          <View style={styles.buttonContent}>
            <Text style={styles.backButtonText}>Back</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Right side - PDF Export, Save for Later, and Complete buttons */}
      <View style={styles.rightButtonSection}>
        {/* PDF Export button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.pdfExportButton,
            (!isPdfExportEnabled || isPdfExporting) && styles.disabledButton,
            { marginRight: isMobile ? 0 : 10 }
          ]}
          onPress={onExportPdf}
          disabled={!isPdfExportEnabled || isPdfExporting}
          testID="pdf-export-button"
        >
          <View style={styles.buttonContent}>
            <MaterialIcons
              name="picture-as-pdf"
              size={20}
              color={(!isPdfExportEnabled || isPdfExporting) ? "#B1B1B1" : "white"}
              style={styles.icon}
            />
            <Text
              style={[
                styles.buttonText,
                (!isPdfExportEnabled || isPdfExporting) &&
                  styles.buttonTextDisabled,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {isPdfExporting ? "Sending..." : "Export as PDF"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Save for Later Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            isSubmitting || isCompleted
              ? styles.disabledButton
              : styles.saveButton,
            Platform.OS === "web" && !(isSubmitting || isCompleted)
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={onSave}
          disabled={isSubmitting || isCompleted}
          accessibilityState={{ disabled: isSubmitting || isCompleted }}
          testID="save-button"
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#034895"
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="save"
                size={20}
                color={isSubmitting || isCompleted ? "#B1B1B1" : "#034895"}
                style={styles.icon}
              />
            )}
            <Text
              style={[
                isSubmitting || isCompleted ? styles.buttonTextDisabled : styles.saveButtonText,
              ]}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {isSubmitting ? "Please wait..." : "Save for Later"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Complete Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            isSubmitting || isCompleted
              ? styles.disabledButton
              : styles.completeButton,
            Platform.OS === "web" && !(isSubmitting || isCompleted)
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={onComplete}
          disabled={isSubmitting || isCompleted}
          accessibilityState={{ disabled: isSubmitting || isCompleted }}
          testID="complete-button"
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color="#B1B1B1"
                style={styles.icon}
              />
            ) : (
              <Text
                style={isSubmitting || isCompleted ? styles.buttonTextDisabled : styles.buttonText}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {isSubmitting ? "Submitting..." : "Complete"}
              </Text>
            )}
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

export default SiteEvalRedirectAction;
